<script setup>
import { Head } from '@inertiajs/vue3'
import PublicLayout from '@/Layouts/PublicLayout.vue'

defineOptions({ layout: PublicLayout })

const blocks = [
  {
    label: 'The people', hue: 20,
    title: 'Forty-plus nations, one country',
    body: 'Maasai and Samburu herders, Swahili traders, Kikuyu farmers, Luo fishers, Turkana, Kalenjin, Kamba and more — Kenya is not one culture but dozens, sharing a land. You meet them as hosts, on their own terms: a Maasai manyatta where the visit is community-run, a Samburu elder’s welcome, a market trader’s easy banter. Never a spectacle — always an exchange.',
  },
  {
    label: 'The coast', hue: 200,
    title: 'A thousand years of the Indian Ocean',
    body: 'Where Africa met Arabia and India, the Swahili world was born — in the carved doors of Lamu, the coral-stone of Mombasa’s Old Town, the dhows still sailing on the evening wind. Swahili is a language, a cuisine, an architecture and a rhythm of life, and it has been here far longer than any border.',
  },
  {
    label: 'The table', hue: 35,
    title: 'Nyama choma, ugali & chai',
    body: 'Food in Kenya is loud and generous. Goat grilled over open fire and eaten with the hands; ugali and sukuma to soak up every drop; coastal biryani and pilau thick with cardamom and coconut; sweet, milky chai at every hour. Eating here is an invitation, and the fastest way into the country.',
  },
  {
    label: 'Rhythm & voice', hue: 300,
    title: 'Benga, Gengetone & the running heart',
    body: 'From the guitar lines of Benga to the street energy of Gengetone, Kenya moves. The Rift Valley breeds the world’s greatest distance runners; the cities breathe a restless creative energy in music, fashion and art. Listen, and the country tells you who it is.',
  },
]
</script>

<template>
  <Head>
    <title>The spirit of Kenya · Kenya2Go</title>
    <meta name="description" content="The people, the Swahili coast, the food and the rhythm — the living culture behind a trip to Kenya.">
  </Head>

  <div class="kp">
    <header class="kp-hero chero">
      <div class="chero__art" aria-hidden="true"></div>
      <div class="kp-hero__inner">
        <p class="kp-eyebrow" style="color: oklch(0.9 0.07 78)">The spirit of Kenya</p>
        <h1 class="kp-hero__title chero__title">Felt, not just seen.</h1>
        <p class="kp-hero__lede chero__lede">Kenya is not a checklist of animals. It’s people, place and rhythm — the things you remember long after the photos. This is the country behind the safari.</p>
      </div>
    </header>

    <section class="kp-section kp-section--narrow cintro">
      <p class="kp-prose">
        We build trips around culture because it’s what makes Kenya, Kenya. Every experience we offer is run by Kenyans — guides, cooks, sailors, artisans, communities — so the money stays here and the encounter is real. Here’s some of what you’ll meet.
      </p>
    </section>

    <section class="cblocks">
      <article v-for="(b, i) in blocks" :key="b.label" class="cblock" :class="{ 'cblock--rev': i % 2 === 1 }">
        <div class="cblock__art" :style="{ '--h': b.hue }"></div>
        <div class="cblock__text">
          <p class="kp-eyebrow">{{ b.label }}</p>
          <h2 class="cblock__title">{{ b.title }}</h2>
          <p class="cblock__body">{{ b.body }}</p>
        </div>
      </article>
    </section>

    <section class="cta2">
      <div class="cta2__inner">
        <h2 class="cta2__title">Step into it.</h2>
        <p class="cta2__lede">The best way to understand Kenya is to be in it. Browse experiences led by the people who live this every day.</p>
        <a href="/tours" class="kp-btn kp-btn--solid">Explore experiences</a>
      </div>
    </section>
  </div>
</template>

<style scoped>
.chero { position: relative; }
.chero__art { position: absolute; inset: 0; z-index: -1;
  background:
    radial-gradient(100% 120% at 85% -10%, oklch(0.7 0.15 35) 0%, transparent 55%),
    linear-gradient(150deg, oklch(0.44 0.11 30) 0%, oklch(0.28 0.06 300) 100%); }
.chero__art::after { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 40%, oklch(0.18 0.04 40 / 0.3)); }
.chero__title { color: oklch(0.99 0.02 85); text-shadow: 0 2px 24px oklch(0.2 0.05 40 / 0.4); }
.chero__lede { color: oklch(0.95 0.03 82); }

.cintro { padding-bottom: 1rem; }

.cblocks { max-width: 70rem; margin: 0 auto; padding: 2rem 1.5rem 1rem; display: flex; flex-direction: column; gap: 2rem; }
.cblock { display: grid; grid-template-columns: 1fr 1.1fr; gap: 2.5rem; align-items: center; }
.cblock--rev .cblock__art { order: 2; }
.cblock__art { aspect-ratio: 4 / 3; border-radius: var(--radius-lg); position: relative; overflow: hidden;
  background: linear-gradient(150deg, oklch(0.68 0.15 calc(var(--h))) 0%, oklch(0.42 0.11 calc(var(--h) + 16)) 100%); }
.cblock__art::after { content: ''; position: absolute; inset: 0; background: radial-gradient(80% 80% at 30% 20%, oklch(1 0 0 / 0.12), transparent 60%); }
.cblock__title { font-family: var(--display); font-weight: 500; font-size: clamp(1.5rem, 3vw, 2.1rem); line-height: 1.08; letter-spacing: -0.015em; margin: 0 0 0.9rem; color: var(--ink); }
.cblock__body { font-size: 1.02rem; line-height: 1.7; color: var(--inkSoft); margin: 0; }

.cta2 { margin-top: 2rem; background: linear-gradient(135deg, oklch(0.42 0.10 45) 0%, oklch(0.30 0.07 38) 100%); color: oklch(0.98 0.02 85); }
.cta2__inner { max-width: 44rem; margin: 0 auto; padding: 4.5rem 1.5rem; text-align: center; }
.cta2__title { font-family: var(--display); font-weight: 400; font-size: clamp(1.9rem, 4.5vw, 2.9rem); margin: 0 0 0.9rem; letter-spacing: -0.02em; }
.cta2__lede { font-size: 1.05rem; line-height: 1.6; color: oklch(0.94 0.03 82); max-width: 32rem; margin: 0 auto 1.8rem; }

@media (max-width: 760px) {
  .cblock { grid-template-columns: 1fr; gap: 1.2rem; }
  .cblock--rev .cblock__art { order: 0; }
}
</style>
